import { Injectable } from '@angular/core';
import { tap } from 'rxjs';
import { ValidationReport } from '@foundation/invoices/models';
import { Receipt, ReceiptConfig } from '@foundation/receipts/models';
import { GenericRepository } from '@foundation/table/state';

/**
 * Reads go through the CRUD router, which has no create, update or delete route: a receipt is
 * written by the admin endpoints and never by the customer it is addressed to. So there is no
 * `createNew…$()` — the copier template generates one (a name prompt and a POST) and a legal
 * document is not something you conjure by typing a title.
 *
 * Drafting one lives here for the same reason it does on invoices, and says the other half of it:
 * a receipt carries none of the mentions an invoice must, because `ReceiptConfig` has no such
 * fields and refuses them.
 */
@Injectable({ providedIn: 'root' })
export class ReceiptsRepository extends GenericRepository<Receipt> {
	/** The resource's own url with `admin/` on it: these are this resource's routes, mounted by
	 * its own api.py — the admin check inside them is the only thing that sets them apart. */
	private _adminUrl = `${this.api_url}/admin`;

	constructor() {
		super('receipt');
	}

	/** What a new receipt would be numbered. Allocates nothing — it is a prediction. */
	nextReceiptNumber$() {
		return this._requestService.getBasic$<{ number: string }>(`${this._adminUrl}/next-number`);
	}

	/** A new receipt as the server proposes it, payment date included — proposed as today, and no
	 * more than proposed: the admin knows when the money actually moved. */
	newReceiptDraft$(userId?: string | null) {
		// With a customer, the buyer comes back filled from the billing details they saved on their
		// own profile: they know their address and their country, and an admin retyping either is
		// an admin getting one of them wrong.
		const forUser = userId ? `?user_id=${userId}` : '';
		return this._requestService.getBasic$<ReceiptConfig>(`${this._adminUrl}/draft-defaults${forUser}`);
	}

	/** Creates the document. It goes over as a partial of the resource — camelCase, which the
	 * model's aliases read as the fields they name, so nothing converts anything on the way. The
	 * server fills in what is its own (number, seller, totals) and answers with the resource, which
	 * goes straight into the store.
	 */
	createReceipt$(partialReceipt: Partial<Receipt>) {
		return this._requestService.post$<Receipt, Partial<Receipt>>(`${this._adminUrl}/drafts`, partialReceipt).pipe(tap((response) => this._keepReceipt(response.result)));
	}

	/** Patches a draft with a partial of the resource — the CRUD patch route's shape, behind the
	 * admin check that is the only reason this endpoint exists. */
	patchReceipt$(receiptId: string, partialReceipt: Partial<Receipt>) {
		return this._requestService
			.post$<Receipt, Partial<Receipt>>(`${this._adminUrl}/drafts/${receiptId}/update`, partialReceipt)
			.pipe(tap((response) => this._keepReceipt(response.result)));
	}

	/** Takes the number, renders, freezes, shares. The only irreversible call in the section, and
	 * it answers with the document it froze — which is what the store then holds. */
	issueReceipt$(receiptId: string) {
		return this._requestService
			.post$<Receipt, Record<string, never>>(`${this._adminUrl}/${receiptId}/issue`, {})
			.pipe(tap((response) => this._keepReceipt(response.result)));
	}

	/** Deletes a draft. An issued document is refused by the endpoint, not by this. */
	deleteReceipt$(receiptId: string) {
		return this._requestService
			.post$<{ deleted: boolean }, Record<string, never>>(`${this._adminUrl}/drafts/${receiptId}/delete`, {})
			.pipe(tap((response) => {
				if (response.result?.deleted) this.store.removeObjectLocally(receiptId, true);
			}));
	}

	/** The document as it would print. A draft comes back watermarked; nothing is stored, so the
	 * store has nothing to hear about. */
	previewReceiptPdf$(partialReceipt: Partial<Receipt>) {
		return this._requestService.postBlob$(`${this._adminUrl}/preview`, partialReceipt);
	}

	/** Checks the receipt. Our own rules always; the outside engine only when asked. */
	checkReceipt$(partialReceipt: Partial<Receipt>, { external = false }: { external?: boolean } = {}) {
		// The document travels in the body, saved or not: what the admin wants checked is what is on
		// their screen, and the copy in the database is a different document while they type.
		return this._requestService.post$<ValidationReport, Partial<Receipt>>(`${this._adminUrl}/check?external=${external}`, partialReceipt, {
			silentError: true,
		});
	}

	/** A local store write: it issues no request, so it is fine on this read-only CRUD router. */
	private _keepReceipt(receipt: Receipt | null | undefined) {
		if (receipt) this.store.upsertObjectLocally(receipt, true);
	}
}
